import { memone } from '@deqo/foundation';
import { ComponentJson, ComponentTuple, property } from '../../../concepts/objects';
import { round } from '../../../platform/geom/Math';
import { Matrix } from '../../../platform/geom/Matrix';
import { Path } from '../../../platform/geom/Path';
import { HasWH, Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { AbstractPhysical } from './AbstractPhysical';
import {
    Figure,
    Physical, PotentialInteraction, RelativeTo, TransformHints, TransformScope
} from './Physical';
import { Resizable } from './Resizable';

const rectangularPhysicalTransformHints = Object.freeze({
    scaleX: true,
    scaleY: true,
    rotate: true,
    translate: true,
});

export interface InteractionRegion {
    readonly name: string;
    readonly area: Rect;
    readonly select?: boolean;
    readonly invoke?: boolean;
    readonly mutate?: boolean;
}

export const InteractionRegion = (name: string, area: Rect, select?: boolean, invoke?: boolean, mutate?: boolean) =>
    ({ name, area, select, invoke, mutate });

const absoluteBounds = (rotation: number) =>
    Matrix.rotate(rotation).apply([
        new Point(-2147483647, -2147483647),
        new Point(2147483647, -2147483647),
        new Point(2147483647, 2147483647),
        new Point(-2147483647, 2147483647),
    ]);

export class RectangularPhysical extends AbstractPhysical implements Physical, Resizable {

    @property({ type: 'number' })
    public x!: number;

    @property({ type: 'number' })
    public y!: number;

    @property({ type: 'number', name: 'rotation' })
    public r!: number;

    @property({ type: 'number' })
    public width!: number;

    @property({ type: 'number' })
    public height!: number;

    public get minWidth() {
        return 0;
    }

    public get minHeight() {
        return 0;
    }

    // @cache()
    protected get localBounds() {
        return new Rect(0, 0, this.width, this.height);
    }

    // @cache()
    protected get parentTransform(): Matrix {
        return Matrix.translate(this.x, this.y).rotate(this.r);
    }

    public size(): Point {
        return new Point(this.width, this.height);
    }

    public resize(width: number, height: number): this;
    public resize(size: Partial<HasWH>): this;
    public resize(...input: any[]): this {
        if (typeof (input[0]) === 'object') {
            return this.resize(input[0].w, input[0].h);
        }
        else {
            this.width = Math.max(input[0] ?? this.width, this.minWidth);
            this.height = Math.max(input[1] ?? this.height, this.minHeight);
        }
    }

    public createFigure(): Figure {
        return this.transform(RelativeTo.Model).apply(this.localBounds);
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const tlt = this.modelLocalTransform();
        from = tlt.apply(from);
        to = tlt.apply(to);

        const [a, b, c] = scope.type === 'scale' ? this.jiggleForPotentialMirror(from, to) : to;

        // width & height vectors
        const wv = Point.vector(a, b);
        const hv = Point.vector(b, c);

        this.setTransformationResult(
            round(a.x, 4), // Position is origin
            round(a.y, 4), // Position is origin
            round(wv.angle(), 4), // Rotation is rotation of width vector
            round(Math.max(this.minWidth, wv.length()), 4), // Width length of width vector
            round(Math.max(this.minHeight, hv.magnitude()), 4), // Height length of height vector,
        );

        return this;
    }

    @memone()
    public queryTransformHints(_singular: boolean) {
        return TransformHints.default;
    }

    public outline(to: RelativeTo | Physical = RelativeTo.Parent): Polyline {
        return this.transform(to).apply(Polyline.from(this.localBounds, true));
    }

    public path(to?: RelativeTo | Physical): Path {
        return this.outline(to).toPath();
    }

    public test(mpt: Point): boolean {
        const lpt = this.toRelative(mpt);
        return this.localBounds.contains(lpt);
    }

    public testInteraction({ mpt }: PotentialInteraction): [boolean, string] {
        // if (type === 'select' || )
        return (this.localBounds.contains(this.toRelative(mpt)))
            ? [true, 'default']
            : [false, 'none'];
    }

    public toShallowTuple(to: RelativeTo | Physical = RelativeTo.Parent): ComponentTuple {
        if (to !== RelativeTo.Parent) {
            return this.applyTransformToTuple(to, super.toShallowTuple());
        }
        else {
            // If relative to parent, just return data as-is since that's how it's stored.
            return super.toShallowTuple();
        }
    }

    public toEmptyTuple(to: RelativeTo | Physical = RelativeTo.Parent): ComponentTuple {
        return this.applyTransformToTuple(to, super.toShallowTuple());
    }

    public toTuple(to: RelativeTo | Physical = RelativeTo.Parent) {
        if (to !== RelativeTo.Parent) {
            // Compute relative transform and decompose into rotation and position
            const srt = this.transform(to).decompose();
            const rotation = srt[1];
            const { x, y } = srt[2];
            // Compute the tuple for this element
            const data = super.toTuple();
            // Overwrite the position and rotation data
            Object.assign(data[2], { x, y, rotation });
            return data;
        }
        else {
            // If relative to parent, just return data as-is since that's how it's stored.
            return super.toTuple();
        }
    }

    public toJson(to: RelativeTo | Physical = RelativeTo.Parent): ComponentJson {
        if (to !== RelativeTo.Parent) {
            const [type, _ref, data] = this.toShallowTuple(to) as any;
            return {
                id: this.id,
                type,
                ...data,
                children: this.children.map(x => x.toJson())
            };
        }
        else {
            // If relative to parent, just return data as-is since that's how it's stored.
            return super.toJson();
        }
    }

    protected applyTransformToTuple(to: RelativeTo | Physical, data: ComponentTuple): ComponentTuple {
        // Compute relative transform and decompose into rotation and position
        const srt = this.transform(to).decompose();
        const rotation = srt[1];
        const { x, y } = srt[2];
        // Compute the tuple for this element
        // Overwrite the position and rotation data
        Object.assign(data[2], { x, y, rotation });
        return data;
    }

    // protected applyAnchors(scope: TransformScope, oldWidth: number, oldHeight: number) {
    //     if (scope.subjects.has(this.parentPhysical)) return;
    //     if ((oldWidth !== this.width || oldHeight !== this.height) && this.children.length) {
    //         const widthDelta = (this.width - oldWidth);
    //         const heightDelta = (this.height - oldHeight);
    //         for (const c of this.children) {
    //             const p = c.as(Physical);
    //             if (!p) continue;
    //             if (scope.subjects.has(p)) continue;
    //             p.applyTransform(scope, computeTransformForAnchor(widthDelta, heightDelta, p));
    //         }
    //     }P
    // }

    protected jiggleForPotentialMirror(from: Figure, to: Figure) {
        if (to.length < 4) return to;
        const originalRotation = Point.vector(from[0], from[1]).angle();
        const [ma, mb, mc, md] = absoluteBounds(originalRotation);
        const abcd = to.slice(0, 4);
        const a = ma.nearestOf(abcd);
        const b = mb.nearestOf(abcd);
        const c = mc.nearestOf(abcd);
        const d = md.nearestOf(abcd);
        return [a, b, c, d, ...to.slice(4)];
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number) {
        // this.x = round(x, 4); // Position is origin
        // this.y = round(y, 4); // Position is origin
        // this.r = round(r, 4); // Rotation is rotation of width vector
        // this.width = round(Math.max(this.minWidth, w), 4); // Width length of width vector
        // this.height = round(Math.max(this.minHeight, h), 4); // Height length of height vector,
        this.x = x; // Position is origin
        this.y = y; // Position is origin
        this.r = r; // Rotation is rotation of width vector
        this.width = Math.max(this.minWidth, w); // Width length of width vector
        this.height = Math.max(this.minHeight, h); // Height length of height vector,
    }
}
